import { cn } from '@/lib/cn';
import { clamp } from 'lodash';
import { useLayoutEffect, useRef, useState } from 'react';

/** Height of an input */
const DEFAULT_MIN_HEIGHT = 40;
/** Reduces maximum height container can be by an amount, to allow some padding */
const ANCESTOR_MAX_HEIGHT_PADDING = 54;
/** When scrolling container to account for resize.
 *  Stop scrolling when scrollTop hits the top of the container, minus this value.
 *  Allowing for some padding at the top of the container.
 * */
const SCROLL_MARGIN_TOP = 50;

type HeightConstraints = {
    min?: number;
    max?: number;
};

type ResizableProps = {
    heightConstraints?: HeightConstraints;
    children: React.ReactNode;
    resizeHandleClassName?: string;
    /** Enable scrolling of any overflow ancestors to keep container in focus while resizing, defaults to `true` */
    scrollAncestor?: boolean;
    className?: string;
};

const clampHeight = (height: number, heightConstraints?: HeightConstraints) => {
    const minHeight = heightConstraints?.min || DEFAULT_MIN_HEIGHT;
    const maxHeight = heightConstraints?.max || Infinity;
    return clamp(height, minHeight, maxHeight);
};

/**
 * Finds the nearest ancestor element with overflow set to auto or scroll.
 */
const findNearestOverflowAncestor = (element: HTMLElement): HTMLElement | null => {
    let currentElement: HTMLElement | null = element;
    while (currentElement) {
        const overflowY = getComputedStyle(currentElement).overflowY;
        if (overflowY === 'auto' || overflowY === 'scroll') {
            return currentElement;
        }
        currentElement = currentElement.parentElement as HTMLElement;
    }
    return null;
};

/**
 * Resizable height component
 *
 * Wrap this component around any content that you want to be resizable in height.
 * It will add a resize handle at the bottom right corner of the content.
 *
 * @example
 * <ResizableHeight heightConstraints={{ min: 100, max: 500 }} resizeHandleClassName='right-[16px] bottom-0'>
 *     <div>
 *         <textarea className='w-full h-full resize-none' />
 *     </div>
 * </ResizableHeight>
 */
export const ResizableHeight = ({
    heightConstraints,
    resizeHandleClassName,
    scrollAncestor = true,
    className,
    children,
    ...props
}: ResizableProps) => {
    const [currentHeight, setCurrentHeight] = useState<number | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        // ensure the container has a height set based on the constraints, when it mounts and when constraints change.
        const container = containerRef.current;

        if (container) {
            const computedStyle = getComputedStyle(container);
            const height = parseFloat(computedStyle.height);
            setCurrentHeight(clampHeight(height, heightConstraints));
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [heightConstraints?.min, heightConstraints?.max]);

    /**
     * Handle resizing container when user clicks and drags the resize handle.
     */
    const handleMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
        const startY = e.clientY;
        const overflowAncestor = findNearestOverflowAncestor(containerRef.current!);
        let lastClientY = startY;
        let lastHeight = currentHeight ?? 0;

        /**
         * Handle resizing container as user moves mouse up and down.
         * 1. Constrains the height of the container based on the provided constraints.
         * 2. Prevents the container from exceeding the height of its overflow ancestor.
         * 3. Scrolls the ancestor element, so that the container remains in the viewport.
         */
        const handleMouseMove = (moveEvent: MouseEvent) => {
            const deltaY = moveEvent.clientY - lastClientY;
            const overflowAncestorRect = overflowAncestor?.getBoundingClientRect();
            const containerRect = containerRef.current?.getBoundingClientRect();
            const targetHeight = lastHeight + deltaY;

            // Height of ancestor element minus padding
            const ancestorMaxHeight = overflowAncestor?.clientHeight
                ? overflowAncestor.clientHeight - ANCESTOR_MAX_HEIGHT_PADDING
                : Infinity;

            // Clamp the target height based on the constraints
            const newHeight = clampHeight(targetHeight, {
                min: heightConstraints?.min,
                max: Math.min(ancestorMaxHeight, heightConstraints?.max ?? Infinity)
            });

            setCurrentHeight(newHeight);

            /**
             * Scroll the ancestor element, so that the container remains in the viewport.
             * Will also scroll user up, to focus container.
             */
            if (scrollAncestor && overflowAncestor && containerRef.current) {
                const currentContainerY = containerRect!.top;
                // Where should the container aim to scroll to on the screen.
                const restingYPosition = overflowAncestorRect!.top + SCROLL_MARGIN_TOP;
                const distanceToRestingPosition = currentContainerY - restingYPosition;
                const scrollAmount = Math.min(Math.abs(distanceToRestingPosition), Math.abs(deltaY));
                /**
                 * Has the bottom of the container collided with the bottom of the ancestor element.
                 * Only scroll down when the bottom of the container is within 5px of the bottom of the ancestor.
                 */
                const overflowAncestorMarginBottom = 5;
                const hasContainerHitAncestorBottom =
                    containerRect!.bottom >= overflowAncestorRect!.bottom - overflowAncestorMarginBottom;

                if (scrollAmount && distanceToRestingPosition > 0 && hasContainerHitAncestorBottom) {
                    overflowAncestor.scrollTop = overflowAncestor.scrollTop + scrollAmount;
                } else if (scrollAmount && distanceToRestingPosition < 0) {
                    overflowAncestor.scrollTop = overflowAncestor.scrollTop - scrollAmount;
                }
            }

            lastClientY = moveEvent.clientY;
            lastHeight = newHeight;
        };

        const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    return (
        <div
            ref={containerRef}
            className={cn('relative w-full', className)}
            style={{
                ...(currentHeight !== null && { height: `${currentHeight}px` }),
                ...(heightConstraints?.min && { minHeight: `${heightConstraints.min}px` }),
                ...(heightConstraints?.max && { maxHeight: `${heightConstraints.max}px` })
            }}
            {...props}
        >
            {children}
            {/* Resize handle */}
            <button
                className={cn(
                    'absolute right-1 bottom-1 w-7 h-7 p-1 !cursor-row-resize items-center justify-center rounded text-textSecondary hover:text-textPrimary transition-colors z-50',
                    resizeHandleClassName
                )}
                onMouseDown={handleMouseDown}
                aria-label='Resize input height'
            >
                <svg viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                    <path
                        d='M21 15L15 21M21 8L8 21'
                        stroke='currentColor'
                        stroke-width='2'
                        stroke-linecap='round'
                        stroke-linejoin='round'
                    />
                </svg>
            </button>
        </div>
    );
};
